import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { MdCheck, MdCheckCircle, MdArrowForward, MdShoppingCart } from 'react-icons/md';
import { useCartStore } from '../store/cartStore';
import { useAuthStore } from '../store/authStore';
import { createCustomerOrder } from '../services/ordersApi';
import {
  listMyAddresses,
  createMyAddress,
  Address as BackendAddress,
} from '../services/addressesApi';
import { AddressAutocompleteInput, ParsedPlace } from '../components/AddressAutocompleteInput';
import Breadcrumb from '../components/shared/Breadcrumb';
import Carousel from '../components/shared/Carousel';
import PopularCategories from '../components/shared/PopularCategories';
import image61 from '../assets/image61.png';
import image62 from '../assets/image62.png';
import image63 from '../assets/image63.png';
import '../styles/CheckoutPage.css';

const PROMO_SLIDES = [
  { id: 1, image: image62, tag: 'WHOLESALE DEALS', title: 'Future-Proof Your Inventory', subtitle: 'Explore premium selections and lock in wholesale pricing today.', cta: { label: 'Shop Now' }, bgColor: '#1A1040' },
  { id: 2, image: image61, tag: 'LIMITED TIME', title: 'Beverages at Bulk Prices', subtitle: 'Stock up on trending drinks with exclusive partner discounts.', cta: { label: 'View Deals' }, bgColor: '#2D1A0E' },
  { id: 3, image: image63, tag: 'ECO-FRIENDLY', title: 'Natural & Organic Range', subtitle: 'Sustainable products your customers love — delivered fast.', cta: { label: 'Explore Now' }, bgColor: '#0A2E1A' },
];

const US_STATES = [
  'Washington', 'California', 'New York', 'Texas', 'Florida', 'Illinois',
  'Pennsylvania', 'Ohio', 'Georgia', 'North Carolina', 'Michigan',
  'New Jersey', 'Virginia', 'Arizona', 'Massachusetts', 'Tennessee',
  'Indiana', 'Missouri', 'Maryland', 'Wisconsin', 'Colorado',
  'Minnesota', 'South Carolina', 'Alabama', 'Louisiana', 'Kentucky',
  'Oregon', 'Oklahoma', 'Connecticut', 'Utah', 'Iowa',
];

const PAYMENT_METHODS = [
  {
    id: 'bank',
    label: 'Direct Bank Transfer',
    desc: 'Make your payment directly into our bank account. Please use your Order ID as the payment reference.',
  },
  {
    id: 'check',
    label: 'Check Payment',
    desc: 'Please send a check to our store address. Your order will be shipped after the check clears.',
  },
  {
    id: 'cod',
    label: 'Cash on Delivery',
    desc: 'Pay with cash upon delivery. A small handling fee may apply.',
  },
];

const TAX_RATE = 0.08;
const FREE_SHIPPING_THRESHOLD = 50;
const SHIPPING_COST = 9.99;

const CheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const { items, getTotal, clearCart } = useCartStore();
  const { user } = useAuthStore();

  /* ── Form state ── */
  const [form, setForm] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    company: '',
    country: 'United States (US)',
    street1: '',
    street2: '',
    city: user?.address?.city || '',
    state: user?.address?.state || 'Washington',
    zip: user?.address?.zipCode || '',
    phone: user?.phone || '',
    email: user?.email || '',
    createAccount: false,
    orderNotes: '',
  });

  const [paymentMethod, setPaymentMethod] = useState('bank');
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');

  /* ── Saved addresses ──
     Loaded from /customers/me/addresses on mount. The user can pick one
     to skip filling the form; "+ New address" leaves the picker null and
     the form fields become the source of truth (a fresh address row is
     created via POST when the order is placed). */
  const [savedAddresses, setSavedAddresses] = useState<BackendAddress[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    listMyAddresses()
      .then((rows) => {
        if (cancelled) return;
        const shipping = rows.filter((a) => a.address_type === 'shipping');
        setSavedAddresses(shipping);
        // Auto-select the default address if there is one.
        const def = shipping.find((a) => a.is_default) ?? shipping[0];
        if (def) {
          setSelectedAddressId(def.id);
          setForm((prev) => ({
            ...prev,
            street1: def.address_line1,
            street2: def.address_line2 ?? '',
            city: def.city,
            state: def.state,
            zip: def.zip_code,
          }));
        }
      })
      .catch(() => { /* not authenticated or new account — fine */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const handlePickAddress = (id: number | null) => {
    setSelectedAddressId(id);
    if (id === null) {
      // "+ New address" — clear the form so the user types fresh values.
      setForm((prev) => ({ ...prev, street1: '', street2: '', city: '', zip: '' }));
      return;
    }
    const addr = savedAddresses.find((a) => a.id === id);
    if (!addr) return;
    setForm((prev) => ({
      ...prev,
      street1: addr.address_line1,
      street2: addr.address_line2 ?? '',
      city: addr.city,
      state: addr.state,
      zip: addr.zip_code,
    }));
  };

  /* ── Computed values ── */
  const subtotal = getTotal();
  const isFreeShipping = subtotal >= FREE_SHIPPING_THRESHOLD;
  const shipping = isFreeShipping ? 0 : SHIPPING_COST;
  const tax = subtotal * TAX_RATE;
  const total = subtotal + shipping + tax;

  const isFormValid = useMemo(() => {
    return (
      form.firstName.trim() &&
      form.lastName.trim() &&
      form.street1.trim() &&
      form.city.trim() &&
      form.state.trim() &&
      form.zip.trim() &&
      form.phone.trim() &&
      form.email.trim()
    );
  }, [form]);

  /* ── Handlers ── */
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handlePlaceOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid || items.length === 0) return;

    // Backend POST /orders (Customer mode) requires a real shipping_address_id.
    // Flow:
    //   1) If the user picked a saved address, use its id directly.
    //   2) Otherwise create a new address row from the form first, then use
    //      the returned id. New rows are NOT marked default — that's a
    //      conscious decision made from the Profile address book.
    //   3) Any error (network, validation) falls back to the legacy local
    //      sessionStorage flow so the demo confirmation still renders. The
    //      console.warn is intentional so this is visible during UAT.
    let backendOrderNumber: string | null = null;
    try {
      let shippingAddressId = selectedAddressId;
      if (!shippingAddressId) {
        // Strip the 2-letter state code if the form holds the full name. The
        // backend addresses table column is CHAR(2).
        const stateCode = form.state.length === 2
          ? form.state.toUpperCase()
          : (form.state.match(/\b([A-Z]{2})\b/) ?? [form.state.slice(0, 2).toUpperCase()])[0];
        const created = await createMyAddress({
          address_type: 'shipping',
          address_line1: form.street1.trim(),
          address_line2: form.street2.trim() || undefined,
          city: form.city.trim(),
          state: stateCode,
          zip_code: form.zip.trim(),
          is_default: savedAddresses.length === 0, // first address auto-defaults
        });
        shippingAddressId = created.id;
        setSavedAddresses((prev) => [created, ...prev]);
        setSelectedAddressId(created.id);
      }

      const createdOrder = await createCustomerOrder({
        shipping_address_id: shippingAddressId,
        items: items.map((it) => ({
          sku_id: it.product.id,
          quantity: it.quantity,
          unit_price: it.product.price,
        })),
      });
      backendOrderNumber = `VIN-${createdOrder.id}`;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Backend order create failed, falling back to local flow:', err);
    }

    const num = backendOrderNumber ?? `VIN-${String(Math.floor(10000 + Math.random() * 90000))}`;
    setOrderNumber(num);
    setOrderPlaced(true);

    // Store order for confirmation page
    const orderData = {
      orderNumber: num,
      items: items.map((item) => ({ name: item.product.name, qty: item.quantity, price: item.product.price, image: item.product.images[0] })),
      subtotal,
      shipping,
      tax,
      total,
      paymentMethod: paymentMethod === 'bank' ? 'Direct Bank Transfer' : paymentMethod === 'check' ? 'Check Payment' : 'Cash on Delivery',
      address: { name: `${form.firstName} ${form.lastName}`, street: form.street1, street2: form.street2, city: form.city, state: form.state, zip: form.zip, country: form.country, phone: form.phone, email: form.email },
      date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    };
    sessionStorage.setItem('lastOrder', JSON.stringify(orderData));
    clearCart();
    navigate('/order-confirmation');
  };

  /* ── Empty cart state ── */
  if (items.length === 0 && !orderPlaced) {
    return (
      <div className="checkout-page">
        <Breadcrumb />
        <section className="checkout-page__empty">
          <div className="checkout-page__empty-card">
            <div className="checkout-page__empty-icon">
              <MdShoppingCart />
            </div>
            <h2 className="checkout-page__empty-heading">Your cart is empty</h2>
            <p className="checkout-page__empty-text">
              Add some products to your cart before proceeding to checkout.
            </p>
            <button
              className="checkout-page__empty-btn"
              onClick={() => navigate('/products')}
            >
              Browse Products <MdArrowForward />
            </button>
          </div>
        </section>
        <section className="checkout-page__carousel">
          <Carousel slides={PROMO_SLIDES} autoPlay interval={5000} />
        </section>
        <PopularCategories />
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <Breadcrumb />

      <form className="checkout-page__main" onSubmit={handlePlaceOrder}>
        <div className="checkout-page__main-inner">
          {/* ──────────── LEFT: Billing Form ──────────── */}
          <div className="checkout-page__billing-card">
            <h2 className="checkout-page__section-title">Billing Detail</h2>

            {/* Saved address picker — only renders when the user has one or more
                addresses on file. Picking a row autofills the form below; the
                "+ Use a new address" pill clears those fields. */}
            {savedAddresses.length > 0 && (
              <div className="checkout-page__form-row checkout-page__form-row--full">
                <div className="checkout-page__form-group">
                  <label className="checkout-page__label">Saved Addresses</label>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                    {savedAddresses.map((addr) => {
                      const active = selectedAddressId === addr.id;
                      return (
                        <button
                          type="button"
                          key={addr.id}
                          onClick={() => handlePickAddress(addr.id)}
                          style={{
                            padding: '8px 12px',
                            borderRadius: 8,
                            border: `1px solid ${active ? '#10B981' : '#E5E7EB'}`,
                            background: active ? '#ECFDF5' : '#fff',
                            cursor: 'pointer',
                            fontSize: 13,
                            textAlign: 'left',
                            color: '#111827',
                          }}
                        >
                          <span style={{ display: 'block', fontWeight: 600 }}>
                            {addr.address_line1}
                            {addr.is_default && (
                              <span style={{ marginLeft: 6, fontSize: 11, color: '#10B981' }}>Default</span>
                            )}
                          </span>
                          <span style={{ display: 'block', color: '#6B7280', fontSize: 12 }}>
                            {addr.city}, {addr.state} {addr.zip_code}
                          </span>
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      onClick={() => handlePickAddress(null)}
                      style={{
                        padding: '8px 12px',
                        borderRadius: 8,
                        border: `1px dashed ${selectedAddressId === null ? '#10B981' : '#9CA3AF'}`,
                        background: selectedAddressId === null ? '#ECFDF5' : '#fff',
                        cursor: 'pointer',
                        fontSize: 13,
                        color: '#374151',
                      }}
                    >
                      + Use a new address
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* First / Last Name */}
            <div className="checkout-page__form-row">
              <div className="checkout-page__form-group">
                <label className="checkout-page__label">
                  First Name<span className="checkout-page__required">*</span>
                </label>
                <input
                  className="checkout-page__input"
                  type="text"
                  name="firstName"
                  value={form.firstName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="checkout-page__form-group">
                <label className="checkout-page__label">
                  Last Name<span className="checkout-page__required">*</span>
                </label>
                <input
                  className="checkout-page__input"
                  type="text"
                  name="lastName"
                  value={form.lastName}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            {/* Company */}
            <div className="checkout-page__form-row checkout-page__form-row--full">
              <div className="checkout-page__form-group">
                <label className="checkout-page__label">Company Name (Optional)</label>
                <input
                  className="checkout-page__input"
                  type="text"
                  name="company"
                  value={form.company}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Country */}
            <div className="checkout-page__form-row checkout-page__form-row--full">
              <div className="checkout-page__form-group">
                <label className="checkout-page__label">
                  Country / Region<span className="checkout-page__required">*</span>
                </label>
                <select
                  className="checkout-page__select"
                  name="country"
                  value={form.country}
                  onChange={handleChange}
                  required
                >
                  <option>United States (US)</option>
                  <option>Canada</option>
                  <option>United Kingdom</option>
                  <option>Australia</option>
                </select>
              </div>
            </div>

            {/* Street Address (Google Places autocomplete — auto-fills city/state/zip) */}
            <div className="checkout-page__form-row checkout-page__form-row--full">
              <div className="checkout-page__form-group">
                <label className="checkout-page__label">
                  Street Address<span className="checkout-page__required">*</span>
                </label>
                <AddressAutocompleteInput
                  className="checkout-page__input"
                  name="street1"
                  placeholder="Start typing your address..."
                  value={form.street1}
                  required
                  onChange={(v) => setForm((prev) => ({ ...prev, street1: v }))}
                  onPlaceSelected={(p: ParsedPlace) => {
                    setForm((prev) => ({
                      ...prev,
                      street1: p.street1 || prev.street1,
                      city: p.city || prev.city,
                      state: p.state || prev.state,
                      zip: p.zip || prev.zip,
                      // Map Google's 2-letter country codes to the selector's labels.
                      country:
                        p.country === 'US' ? 'United States (US)' :
                        p.country === 'CA' ? 'Canada' :
                        p.country === 'GB' ? 'United Kingdom' :
                        p.country === 'AU' ? 'Australia' :
                        prev.country,
                    }));
                  }}
                />
              </div>
            </div>
            <div className="checkout-page__form-row checkout-page__form-row--full">
              <div className="checkout-page__form-group">
                <input
                  className="checkout-page__input"
                  type="text"
                  name="street2"
                  placeholder="Apartment, suite, unit, etc (Optional)"
                  value={form.street2}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Town / City */}
            <div className="checkout-page__form-row checkout-page__form-row--full">
              <div className="checkout-page__form-group">
                <label className="checkout-page__label">
                  Town / City<span className="checkout-page__required">*</span>
                </label>
                <input
                  className="checkout-page__input"
                  type="text"
                  name="city"
                  value={form.city}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            {/* State + Zip */}
            <div className="checkout-page__form-row">
              <div className="checkout-page__form-group">
                <label className="checkout-page__label">
                  State / County<span className="checkout-page__required">*</span>
                </label>
                <select
                  className="checkout-page__select"
                  name="state"
                  value={form.state}
                  onChange={handleChange}
                  required
                >
                  {US_STATES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="checkout-page__form-group">
                <label className="checkout-page__label">
                  Zip Code<span className="checkout-page__required">*</span>
                </label>
                <input
                  className="checkout-page__input"
                  type="text"
                  name="zip"
                  value={form.zip}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            {/* Phone + Email */}
            <div className="checkout-page__form-row">
              <div className="checkout-page__form-group">
                <label className="checkout-page__label">
                  Phone Number<span className="checkout-page__required">*</span>
                </label>
                <input
                  className="checkout-page__input"
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="checkout-page__form-group">
                <label className="checkout-page__label">
                  Email Address<span className="checkout-page__required">*</span>
                </label>
                <input
                  className="checkout-page__input"
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            {/* Create account checkbox */}
            <div className="checkout-page__checkbox-row">
              <input
                className="checkout-page__checkbox"
                type="checkbox"
                id="createAccount"
                name="createAccount"
                checked={form.createAccount}
                onChange={handleChange}
              />
              <label className="checkout-page__checkbox-label" htmlFor="createAccount">
                Create an account?
              </label>
            </div>

            {/* ── Additional Information ── */}
            <h2 className="checkout-page__section-title checkout-page__section-title--additional">
              Additional Information
            </h2>

            <div className="checkout-page__form-row checkout-page__form-row--full">
              <div className="checkout-page__form-group">
                <label className="checkout-page__label">Order Notes (Optional)</label>
                <textarea
                  className="checkout-page__textarea"
                  name="orderNotes"
                  placeholder="Note about your order, e.g. special note for delivery"
                  value={form.orderNotes}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          {/* ──────────── RIGHT: Order Summary ──────────── */}
          <div className="checkout-page__summary-card">
            <h2 className="checkout-page__summary-title">Your Order</h2>

            {/* Items table */}
            <table className="checkout-page__order-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={`${item.product.id}-${item.selectedColor}-${item.selectedSize}`}>
                    <td>
                      <span className="checkout-page__item-name">{item.product.name}</span>
                      {' '}
                      <span className="checkout-page__item-qty">&times; {item.quantity}</span>
                    </td>
                    <td>${(item.product.price * item.quantity).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Summary rows */}
            <div className="checkout-page__summary-rows">
              <div className="checkout-page__summary-row">
                <span>Subtotal</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              <div className="checkout-page__summary-row">
                <span>Shipping</span>
                {isFreeShipping ? (
                  <span className="checkout-page__free-shipping">Free shipping</span>
                ) : (
                  <span>${shipping.toFixed(2)}</span>
                )}
              </div>
              <div className="checkout-page__summary-row">
                <span>Tax (8%)</span>
                <span>${tax.toFixed(2)}</span>
              </div>
            </div>

            <div className="checkout-page__summary-divider" />

            <div className="checkout-page__summary-rows">
              <div className="checkout-page__summary-row checkout-page__summary-row--total">
                <span>Total</span>
                <span>${total.toFixed(2)}</span>
              </div>
            </div>

            {/* Payment methods */}
            <div className="checkout-page__payment-section">
              <h3 className="checkout-page__payment-title">Payment Method</h3>
              {PAYMENT_METHODS.map((pm) => (
                <div className="checkout-page__payment-option" key={pm.id}>
                  <label className="checkout-page__payment-radio">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value={pm.id}
                      checked={paymentMethod === pm.id}
                      onChange={() => setPaymentMethod(pm.id)}
                    />
                    <span>{pm.label}</span>
                  </label>
                  {paymentMethod === pm.id && (
                    <p className="checkout-page__payment-desc">{pm.desc}</p>
                  )}
                </div>
              ))}
            </div>

            {/* Place Order */}
            <button
              type="submit"
              className="checkout-page__place-order-btn"
              disabled={!isFormValid}
            >
              Place Order <MdArrowForward />
            </button>

            <p className="checkout-page__privacy-note">
              Your personal data will be used to process your order, support your experience
              throughout this website, and for other purposes described in our privacy policy.
            </p>
          </div>
        </div>
      </form>

      {/* ── Carousel ── */}
      <section className="checkout-page__carousel">
        <Carousel slides={PROMO_SLIDES} autoPlay interval={5000} />
      </section>

      {/* ── Popular Categories ── */}
      <PopularCategories />

      {/* ── Success Overlay ── */}
      {orderPlaced && (
        <div className="checkout-page__success-overlay">
          <div className="checkout-page__success-card">
            <div className="checkout-page__success-icon">
              <MdCheckCircle />
            </div>
            <h2 className="checkout-page__success-heading">Order Placed Successfully!</h2>
            <p className="checkout-page__success-text">
              Your order <strong>{orderNumber}</strong> has been placed
            </p>
            <div className="checkout-page__success-actions">
              <button
                className="checkout-page__success-btn checkout-page__success-btn--primary"
                onClick={() => navigate('/products')}
              >
                Continue Shopping <MdArrowForward />
              </button>
              <button
                className="checkout-page__success-btn checkout-page__success-btn--outline"
                onClick={() => navigate('/orders')}
              >
                View Orders
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckoutPage;
